import { useMemo, useState } from "react";
import {
  Button,
  Card,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message as antMessage,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useAuth } from "shared/contexts/AuthProvider";
import { replicationRulesService } from "../services/orgStorageService";
import { getErrorDetail } from "./OrgStorageSection";
import { BUTTON_STYLES } from "../constants";

const { Text } = Typography;

/**
 * Replication rules of the organization, aggregated across its storages
 * (rules live on their SOURCE storage). Files that land on the source are
 * copied or moved to the target by the platform worker / the org's agent.
 *
 * @param {string} orgId - Organization ID
 * @param {Array} storages - Storage rows (including embedded replication_rules)
 * @param {boolean} canManage - When false, read-only
 * @param {Function} translate - Translation function
 * @param {Function} onChanged - Reload callback after any rule change
 */
export const ReplicationRulesCard = ({
  orgId,
  storages,
  canManage = false,
  translate = (key) => key,
  onChanged,
}) => {
  const { authFetch } = useAuth();
  const [adding, setAdding] = useState(false);
  const [newRule, setNewRule] = useState({
    source_storage_id: null,
    target_storage_id: null,
    mode: "copy",
  });

  const storageName = useMemo(() => {
    const names = {};
    storages.forEach((s) => {
      names[s.storage_id] = s.name;
    });
    return (id) => names[id] || id;
  }, [storages]);

  const rules = useMemo(
    () =>
      storages.flatMap((s) =>
        (s.replication_rules || []).map((rule) => ({
          ...rule,
          source_storage_id: s.storage_id,
          key: `${s.storage_id}:${rule.rule_id}`,
        })),
      ),
    [storages],
  );

  const storageOptions = storages
    .filter((s) => s.enabled)
    .map((s) => ({ value: s.storage_id, label: s.name }));

  const handleAdd = async () => {
    if (!newRule.source_storage_id || !newRule.target_storage_id) {
      antMessage.warning(translate("organizations.replication.selectBoth"));
      return;
    }
    try {
      setAdding(true);
      await replicationRulesService.create(
        orgId,
        newRule.source_storage_id,
        {
          target_storage_id: newRule.target_storage_id,
          mode: newRule.mode,
          enabled: true,
        },
        authFetch,
      );
      antMessage.success(translate("organizations.replication.addSuccess"));
      setNewRule({ source_storage_id: null, target_storage_id: null, mode: "copy" });
      onChanged?.();
    } catch (error) {
      antMessage.error(
        translate("organizations.replication.addFailed") +
          getErrorDetail(error),
      );
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (rule, enabled) => {
    try {
      await replicationRulesService.update(
        orgId,
        rule.source_storage_id,
        rule.rule_id,
        { enabled },
        authFetch,
      );
      onChanged?.();
    } catch (error) {
      antMessage.error(
        translate("organizations.replication.updateFailed") +
          getErrorDetail(error),
      );
    }
  };

  const handleDelete = async (rule) => {
    try {
      await replicationRulesService.remove(
        orgId,
        rule.source_storage_id,
        rule.rule_id,
        authFetch,
      );
      antMessage.success(translate("organizations.replication.deleteSuccess"));
      onChanged?.();
    } catch (error) {
      antMessage.error(
        translate("organizations.replication.deleteFailed") +
          getErrorDetail(error),
      );
    }
  };

  const columns = [
    {
      title: translate("organizations.replication.source"),
      dataIndex: "source_storage_id",
      key: "source",
      render: (id) => storageName(id),
    },
    {
      title: translate("organizations.replication.target"),
      dataIndex: "target_storage_id",
      key: "target",
      render: (id) => storageName(id),
    },
    {
      title: translate("organizations.replication.mode"),
      dataIndex: "mode",
      key: "mode",
      render: (mode) => (
        <Tag color={mode === "move" ? "volcano" : "blue"}>
          {mode === "move"
            ? translate("organizations.replication.move")
            : translate("organizations.replication.copy")}
        </Tag>
      ),
    },
    {
      title: translate("organizations.storage.enabled"),
      dataIndex: "enabled",
      key: "enabled",
      align: "center",
      render: (enabled, rule) => (
        <Switch
          size="small"
          checked={enabled}
          disabled={!canManage}
          onChange={(checked) => handleToggle(rule, checked)}
        />
      ),
    },
    ...(canManage
      ? [
          {
            title: translate("organizations.storages.actions"),
            key: "actions",
            render: (_, rule) => (
              <Popconfirm
                title={translate("organizations.replication.deleteConfirm")}
                onConfirm={() => handleDelete(rule)}
              >
                <Button size="small" danger>
                  {translate("organizations.storages.delete")}
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card
      size="small"
      title={translate("organizations.replication.title")}
      style={{ marginBottom: 16 }}
    >
      <Text
        type="secondary"
        style={{ display: "block", marginBottom: 8, fontSize: 12 }}
      >
        {translate("organizations.replication.subtitle")}
      </Text>
      <Table
        size="small"
        columns={columns}
        dataSource={rules}
        pagination={false}
        locale={{ emptyText: translate("organizations.replication.empty") }}
        style={{ marginBottom: canManage ? 12 : 0 }}
      />
      {canManage && (
        <Space wrap>
          <Select
            style={{ minWidth: 160 }}
            placeholder={translate("organizations.replication.source")}
            value={newRule.source_storage_id}
            onChange={(value) =>
              setNewRule((prev) => ({ ...prev, source_storage_id: value }))
            }
            options={storageOptions}
          />
          <Text type="secondary">→</Text>
          <Select
            style={{ minWidth: 160 }}
            placeholder={translate("organizations.replication.target")}
            value={newRule.target_storage_id}
            onChange={(value) =>
              setNewRule((prev) => ({ ...prev, target_storage_id: value }))
            }
            options={storageOptions.filter(
              (option) => option.value !== newRule.source_storage_id,
            )}
          />
          <Select
            style={{ minWidth: 100 }}
            value={newRule.mode}
            onChange={(value) =>
              setNewRule((prev) => ({ ...prev, mode: value }))
            }
            options={[
              {
                value: "copy",
                label: translate("organizations.replication.copy"),
              },
              {
                value: "move",
                label: translate("organizations.replication.move"),
              },
            ]}
          />
          <Button
            icon={<PlusOutlined />}
            loading={adding}
            style={BUTTON_STYLES.add}
            onClick={handleAdd}
          >
            {translate("organizations.replication.add")}
          </Button>
        </Space>
      )}
    </Card>
  );
};
